import React from 'react'
import { motion } from 'framer-motion'

/* Animated DNA double helix SVG */
export const DNAHelix = ({ className = '' }) => (
  <svg viewBox="0 0 120 400" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Left strand */}
    <motion.path
      d="M30 0 C30 40, 90 40, 90 80 C90 120, 30 120, 30 160 C30 200, 90 200, 90 240 C90 280, 30 280, 30 320 C30 360, 90 360, 90 400"
      stroke="#00a86b"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.6 }}
      transition={{ duration: 3, ease: 'easeInOut' }}
    />
    {/* Right strand */}
    <motion.path
      d="M90 0 C90 40, 30 40, 30 80 C30 120, 90 120, 90 160 C90 200, 30 200, 30 240 C30 280, 90 280, 90 320 C90 360, 30 360, 30 400"
      stroke="#0097c7"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 0.5 }}
      transition={{ duration: 3, ease: 'easeInOut', delay: 0.3 }}
    />
    {/* Rungs / base pairs */}
    {[40, 80, 120, 160, 200, 240, 280, 320, 360].map((y, i) => (
      <motion.line
        key={i}
        x1={i % 2 === 0 ? 45 : 40}
        y1={y}
        x2={i % 2 === 0 ? 75 : 80}
        y2={y}
        stroke={i % 3 === 0 ? '#f7931e' : '#00a86b'}
        strokeWidth="1.5"
        strokeLinecap="round"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 0.4 }}
        transition={{ duration: 0.6, delay: 1 + i * 0.15 }}
        style={{ transformOrigin: 'center' }}
      />
    ))}
    {/* Node dots at base pair junctions */}
    {[40, 120, 200, 280, 360].map((y, i) => (
      <motion.circle
        key={`dot-${i}`}
        cx={i % 2 === 0 ? 60 : 60}
        cy={y}
        r="3"
        fill="#00a86b"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.4, delay: 1.5 + i * 0.2 }}
      />
    ))}
  </svg>
)

/* Floating molecule SVG */
export const MoleculeCluster = ({ className = '' }) => (
  <svg viewBox="0 0 200 200" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Central atom */}
    <motion.circle cx="100" cy="100" r="8" fill="#00a86b" opacity="0.3"
      animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
    <circle cx="100" cy="100" r="4" fill="#00a86b" opacity="0.6" />

    {/* Bonds and outer atoms */}
    {[
      { cx: 60, cy: 60, color: '#0097c7' },
      { cx: 145, cy: 55, color: '#f7931e' },
      { cx: 150, cy: 130, color: '#00a86b' },
      { cx: 55, cy: 140, color: '#0097c7' },
      { cx: 100, cy: 40, color: '#f7931e' },
    ].map((atom, i) => (
      <g key={i}>
        <motion.line x1="100" y1="100" x2={atom.cx} y2={atom.cy}
          stroke={atom.color} strokeWidth="1.5" opacity="0.25"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, delay: 0.5 + i * 0.2 }} />
        <motion.circle cx={atom.cx} cy={atom.cy} r="5"
          fill={atom.color} opacity="0.4"
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 1 + i * 0.15 }} />
      </g>
    ))}
  </svg>
)

/* Hex grid pattern SVG for bio aesthetic */
export const HexGrid = ({ className = '' }) => (
  <svg viewBox="0 0 300 260" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {[
      [50, 30], [110, 30], [170, 30], [230, 30],
      [80, 82], [140, 82], [200, 82], [260, 82],
      [50, 134], [110, 134], [170, 134], [230, 134],
      [80, 186], [140, 186], [200, 186], [260, 186],
    ].map(([cx, cy], i) => (
      <motion.path
        key={i}
        d={`M${cx} ${cy - 22}L${cx + 20} ${cy - 11}L${cx + 20} ${cy + 11}L${cx} ${cy + 22}L${cx - 20} ${cy + 11}L${cx - 20} ${cy - 11}Z`}
        stroke="#00a86b"
        strokeWidth="1"
        fill="none"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.12, scale: 1 }}
        transition={{ duration: 0.5, delay: i * 0.06 }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
    ))}
  </svg>
)

const BackgroundElements = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
      {/* Subtle green radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-hero opacity-60" />

      {/* DNA Helix — left */}
      <div className="absolute left-[5%] top-1/2 -translate-y-1/2 opacity-20">
        <DNAHelix className="w-24 h-auto" />
      </div>

      {/* DNA Helix — right */}
      <div className="absolute right-[5%] top-1/2 -translate-y-1/2 opacity-15 scale-x-[-1]">
        <DNAHelix className="w-20 h-auto" />
      </div>

      {/* Floating molecules */}
      <motion.div className="absolute top-[15%] right-[12%]" animate={{ y: [-10, 15, -10] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
        <MoleculeCluster className="w-32 h-32 opacity-20" />
      </motion.div>
      <motion.div className="absolute bottom-[20%] left-[8%]" animate={{ y: [10, -15, 10] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}>
        <MoleculeCluster className="w-24 h-24 opacity-15" />
      </motion.div>

      {/* Hex grid — bottom right */}
      <div className="absolute bottom-10 right-[3%] opacity-25">
        <HexGrid className="w-64 h-auto" />
      </div>

      {/* Floating accent dots */}
      <motion.div className="absolute top-[25%] left-[20%] w-2 h-2 rounded-full bg-primary" animate={{ y: [-8, 12, -8], opacity: [0.1, 0.3, 0.1] }} transition={{ duration: 5, repeat: Infinity }} />
      <motion.div className="absolute top-[60%] right-[25%] w-3 h-3 rounded-full bg-[#0097c7]" animate={{ y: [10, -12, 10], opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 6, repeat: Infinity, delay: 1.5 }} />
      <motion.div className="absolute bottom-[35%] left-[30%] w-1.5 h-1.5 rounded-full bg-[#f7931e]" animate={{ y: [-12, 8, -12], opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 7, repeat: Infinity, delay: 2 }} />
    </div>
  )
}

export default BackgroundElements
